/**
 * 下注操作区（§11.5）。玩家在整局里唯一真正「操作」的地方，手感决定成败。
 *
 * 放置方式：本组件只渲染 dock 的**内容**，由牌桌那一侧塞进 `<div className="dock">` 里：
 *   <div className="dock"><ActionDock onSubmit={submitIntent} /></div>
 * 提交通道用 prop 而不是直接写 store：本地传手机模式下意图要直接喂给浏览器内的引擎，
 * 联机模式要经过 outbox 和 actionToken，两者的差异不该渗进 UI。
 *
 * ── 这一版重做了形态（用户抱怨的 P3：「按钮逻辑奇怪，而且占了非常多空间」）──
 *
 * 旧版把「主按钮 + 快捷档位 + 滑块」**全部常驻**，内容高 147px，非自己回合时那一大块
 * 还照样占位。现在只有两态：
 *
 *   收起态（默认）  一行主按钮：弃牌 / 过牌·跟注 $X / 加注。内容高恒为 --dock-h
 *                  （三档密度统一 64px），底部安全区另算。
 *                  跟注金额直接印在按钮上 —— 玩家不该为了知道要付多少去心算。
 *   展开态          点「加注」才浮出**底部抽屉**（.dock__sheet，absolute 锚在 dock 上边缘），
 *                  尺度面板在抽屉里，dock 盒高不变 —— 所以牌桌不会被顶上去，
 *                  舞台高度在整手牌里恒定，座位几何不会因为开合面板而重算。
 *   非自己回合      内容仍是一条轻量状态行，但 dock 保持 `actions` 的固定高度；
 *                  预选按钮落在主按钮同一条底部基线上，切回行动时牌桌不跳。
 *
 * 本文件承担的红线：
 *  - #21 能否加注只看 `canRaise`（推导全在 buildDockPlan 里，这里不再自己判）
 *  - #18 加注金额恒为 raise-to，文案一律「加注**到**」
 *  - #48 滑块拖动不走 React state（在 RaiseControl 里）
 *  - #46 按下反馈只改 transform / filter，不引起布局位移
 *  - §11.5 防误触五条：不能免费弃牌 / All-in 长按 / 220ms 冷却 / pointerup 触发 / 单指约束
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { ActionIntent, Chips, LegalActions } from '@poker/shared'

import './action.css'
import type { DockState } from '../App.js'
import { fireHaptic, type HapticPattern, type PulseTarget } from '../fx/haptics.js'
import { IconClock, IconClose } from '../icons/index.js'
import { useSettingsStore, useTableStore, useUiStore } from '../store/index.js'
import {
  ACTION_COOLDOWN_MS,
  ESCAPE_FOLD_HOLD_MS,
  HOLD_CONFIRM_MS,
  HOLD_ROLLBACK_MS,
  buildDockPlan,
  defaultRaiseTo,
  formatChips,
  isAllInTo,
  type AllinConfirmMode,
  type DockButton,
  type DockButtonKind,
} from './useBetSizing.js'
import {
  PRE_ACTION_DELAY_MS,
  PRE_ACTION_SKIP_DELAY_UNDER_MS,
  PreActionBar,
  computeProspect,
  resolvePreAction,
  type PreActionChoice,
} from './PreAction.js'
import { RaiseControl } from './RaiseControl.js'
import { typingGuardActive } from '../talk/typingGuard.js'

/** 提交后多久还没被确认就提示「网络较慢」 */
const SLOW_HINT_MS = 800
/** 多久之后把按钮放回可点状态：重发的是**完全相同**的 intent，服务端幂等 */
const PENDING_RECOVER_MS = 3000

/**
 * 主按钮行的宽度配重（flex-grow）。**不是等宽** —— 三个按钮的重要性不同：
 * 跟注/过牌是这一屏唯一的主 CTA，最宽最醒目；加注次之；弃牌最窄且离主按钮更远
 * （额外间距见 action.css 的 `.adock__btn[data-rank='quiet']`），防误触。
 *
 * ⚠ 只在有三个按钮时生效。加注按钮不渲染（canRaise=false）时只剩两个，
 * 按需求**均分宽度** —— 两个按钮再配重，弃牌会窄到不好按。
 */
const GROW: Readonly<Record<DockButtonKind, number>> = {
  fold: 0.85,
  check: 1.35,
  call: 1.35,
  raise: 1,
}

/** 按钮的视觉层级。一屏只有一个 primary（UI 规则 HIGH：每屏只有一个主 CTA）。 */
type ButtonRank = 'primary' | 'secondary' | 'quiet'

const RANK: Readonly<Record<DockButtonKind, ButtonRank>> = {
  fold: 'quiet',
  check: 'primary',
  call: 'primary',
  raise: 'secondary',
}

/**
 * 触觉反馈一律走 `fx/haptics`，**绝不直接调 `navigator.vibrate`**（DEVICES.md R4）。
 *
 * 直接调 + 可选链确实不会报错，但 iOS（实测机 D2 = iPhone + 微信 WKWebView）
 * 上恒为 undefined —— 于是「长按确认全下」这种最需要确认感的操作，
 * 在一半用户那里是**完全没有反馈**的，而且在 Mac 上调试永远发现不了。
 * `fireHaptic` 做能力检测，震不了就降级成 120ms 的按钮压缩 + 一声短音。
 * `target` 是被按的那个元素：视觉补偿要落在手指底下才有意义。
 */
function buzz(enabled: boolean, pattern: HapticPattern, target?: PulseTarget | null): void {
  fireHaptic(pattern, enabled, target ?? null)
}

/** 服务端渲染（渲染冒烟测试跑在 node 环境）下没有 layout 阶段，降级成 useEffect 免掉警告 */
const useIsoLayoutEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect

/**
 * 把 dock 的形态写到**外层 `.dock`** 上。
 *
 * 为什么是命令式地写祖先节点，而不是由 TableScene 传下来：形态只有 ActionDock 自己知道
 * （轮到谁、加注抽屉开没开），而 `.dock` 是别的模块渲染的。做成「父组件传 state 下来 +
 * 子组件回调上去」等于让两个模块共同持有同一个真相，必然漂移。
 * 这里写的是一个**纯展示属性**（CSS 靠它切四态），卸载时原样还原，不影响任何业务状态。
 *
 * `onChange` 仍然照发：将来 TableScene 想自己接管（比如叠加断线横幅）时有现成的口子。
 */
function useDockState(
  state: DockState,
  anchor: { readonly current: HTMLElement | null },
  onChange?: (next: DockState) => void,
): void {
  useIsoLayoutEffect(() => {
    const el = anchor.current
    if (el === null || typeof el.closest !== 'function') return undefined
    const dock = el.closest('.dock')
    if (!(dock instanceof HTMLElement)) return undefined
    const prev = dock.dataset['state']
    dock.dataset['state'] = state

    /**
     * ★ 同一个真相也写到 `.app-shell` 的 data-dock 上。
     *
     * Toast 的底部偏移按 `.app-shell[data-dock]` 让开操作区（session.css），
     * 而 App.tsx 以前在牌桌视图里把它**恒写成 'actions'** —— 注释自己都写着
     * 「壳层不知道它此刻是哪一态，按 actions 记账」。于是等待室里真实 dock 只有
     * 约 51px（bar 态 min-height:0），Toast 却按 --dock-h 抬高，
     * 凭空悬在舞台底沿上方约 87px，正落在「加 AI 补位」那一行上。
     *
     * 形态只有 ActionDock 自己知道，所以由它一处写两个节点；App.tsx 在牌桌视图
     * 不再写 data-dock，避免两个 owner 打架。ActionDock 没挂载时该属性缺席，
     * Toast 退回贴底部安全区 —— 那时本来也没有操作区要让。
     */
    const shell = el.closest('.app-shell')
    const prevShell = shell instanceof HTMLElement ? shell.dataset['dock'] : undefined
    if (shell instanceof HTMLElement) shell.dataset['dock'] = state

    return () => {
      if (prev === undefined) delete dock.dataset['state']
      else dock.dataset['state'] = prev
      if (shell instanceof HTMLElement) {
        if (prevShell === undefined) delete shell.dataset['dock']
        else shell.dataset['dock'] = prevShell
      }
    }
  }, [state, anchor])

  useEffect(() => {
    onChange?.(state)
  }, [state, onChange])
}

export interface ActionDockProps {
  /** 提交意图。调用方负责喂给本地引擎或 WS outbox。 */
  readonly onSubmit: (intent: ActionIntent) => void
  /** 覆盖 store 里的 legal（回放 / storybook 用）；省略则订阅 tableStore */
  readonly legal?: LegalActions | null
  /** 覆盖 store 里的大盲（算取整步长用）；省略则取 view.config.bigBlind */
  readonly bigBlind?: Chips
  readonly allinConfirm?: AllinConfirmMode
  /** 关掉预选条（本地传手机模式其实用不上，手机就在自己手里） */
  readonly preAction?: boolean
  /** 距离 displayDeadlineAt 还剩多少毫秒；null = 没有倒计时（turnMs === 0） */
  readonly remainingMs?: number | null
  /** dock 形态变化时通知外层（外层想自己记账时用；不传也会直接写 DOM 属性） */
  readonly onDockStateChange?: (next: DockState) => void
  /**
   * 空闲条上的一颗「回到牌桌」按钮：'sit' = 坐下（无座且有空位），'return' = 回到牌局（坐出中）。
   * 由外层按 PlayerView 算好（room/roomDerive.ts 的 idleDockAction），本组件只负责画。
   * 牌局进行中全客户端原来没有任何入座 / 回桌入口 —— 都锁在只在 lobby/paused 渲染的等待室里。
   */
  readonly idleCta?: { readonly kind: 'sit' | 'return'; readonly onPress: () => void } | null
  /** 这一手没发到自己时的原因（暂离 / 筹码用完 / 下一手发牌）。优先于默认的「等待 X 行动」 */
  readonly idleNote?: string | null
  /**
   * 空闲条右端的附加控件（联机模式的「催一下」）。本地传手机模式不传。
   * 空闲条上有「坐下 / 回到牌局」时不画：自己这手没发到牌，催别人不是这时该做的事，
   * 而金色的「催一下」挨着金色主按钮既抢主位、又容易被连点误触。
   */
  readonly idleTrail?: ReactNode
  /** 暂时替换空闲条上的状态文字（新聊天的 4 秒快讯）；null = 照常显示「等待 X 行动」 */
  readonly statusOverride?: ReactNode
}

// ============================================================================
// 主按钮：pointerup 触发 + 单指约束 + 长按确认
// ============================================================================

interface PressButtonProps {
  readonly btn: DockButton
  readonly disabled: boolean
  readonly haptics: boolean
  readonly holdMs?: number
  /** flex-grow。三按钮时按 GROW 配重，两按钮时一律 1（均分） */
  readonly grow?: number
  readonly rank?: ButtonRank
  /** 收起态的「加注」只是展开抽屉，右上角挂一个向上的角标暗示这一点 */
  readonly expands?: boolean
  /** 整个 dock 共享的「当前活跃指针」。第一根手指以外的按下一律忽略。 */
  readonly pointerRef: { current: number | null }
  readonly onFire: () => void
  readonly children?: ReactNode
}

/** 向上的角标：告诉玩家这个按钮**会展开面板**而不是直接出手。禁用 emoji，一律 SVG。 */
function ChevronUp(): ReactNode {
  return (
    <svg
      className="adock__btn-chev"
      viewBox="0 0 20 20"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5 10 7.5l5 5" />
    </svg>
  )
}

function PressButton({
  btn,
  disabled,
  haptics,
  holdMs = HOLD_CONFIRM_MS,
  grow = 1,
  rank = 'primary',
  expands = false,
  pointerRef,
  onFire,
  children,
}: PressButtonProps): ReactNode {
  const holdRef = useRef<HTMLSpanElement>(null)
  const animRef = useRef<Animation | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firedRef = useRef(false)
  /** 本按钮此刻是否持有 dock 的指针所有权 */
  const ownedRef = useRef(false)

  const stopHold = useCallback((rollback: boolean): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const a = animRef.current
    animRef.current = null
    if (a === null) return
    const t = typeof a.currentTime === 'number' ? a.currentTime : 0
    a.cancel()
    const el = holdRef.current
    if (rollback && el !== null && t > 0 && typeof el.animate === 'function') {
      const p = Math.min(1, t / holdMs)
      el.animate(
        [{ transform: `scaleX(${p})` }, { transform: 'scaleX(0)' }],
        { duration: HOLD_ROLLBACK_MS, easing: 'ease-out', fill: 'forwards' },
      )
    }
  }, [holdMs])

  // 卸载时既要清定时器，也要把指针所有权还回去：按着按钮时牌桌换了一手，
  // 按钮连同它的 pointerup 一起消失，activePointerId 会永远卡住，整个 dock 再也点不动
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      if (ownedRef.current) {
        ownedRef.current = false
        pointerRef.current = null
      }
    },
    [pointerRef],
  )

  const handleDown = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    if (disabled) return
    // 打字护栏：刚在键盘上按过键，这一下多半是打字的手指蹭到了上面一排（见 talk/typingGuard.ts）
    if (typingGuardActive()) return
    // 单指约束：两根手指同时按 Fold 和 Call 会产生两条同 actionNo 的意图，
    // 服务端幂等只认先到的那条——而先到的可能正是玩家不想要的那个
    if (pointerRef.current !== null) return
    pointerRef.current = e.pointerId
    ownedRef.current = true
    firedRef.current = false
    const el = e.currentTarget
    el.dataset['pressed'] = 'on'
    // 触摸指针默认被**隐式捕获**：不释放的话手指滑出按钮后 pointerup 仍落在本按钮上，
    // 「校验落点仍在按钮内」就形同虚设。释放后边界判定交还给 pointerleave。
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
    if (!btn.confirm) return
    // 真正决定是否触发的是定时器；WAAPI 只负责那圈进度描边，动画不可用也不影响正确性
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      firedRef.current = true
      buzz(haptics, 'commit', el)
      onFire()
    }, holdMs)
    const bar = holdRef.current
    if (bar !== null && typeof bar.animate === 'function') {
      animRef.current = bar.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: holdMs, easing: 'linear', fill: 'forwards' },
      )
    }
  }

  const release = (el: HTMLButtonElement): void => {
    pointerRef.current = null
    ownedRef.current = false
    delete el.dataset['pressed']
  }

  const handleUp = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    if (pointerRef.current !== e.pointerId) return
    release(e.currentTarget)
    if (btn.confirm) {
      if (!firedRef.current) stopHold(true)
      return
    }
    onFire()
  }

  const handleAbort = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    if (pointerRef.current !== e.pointerId) return
    release(e.currentTarget)
    stopHold(true)
  }

  /**
   * 展开型按钮（收起态的「加注」）上**不带金额**，label 里那个「到」就成了半句话。
   * buildDockPlan 的 label 是给「加注到 400」这种带数字的排版用的，这里去掉尾字，
   * 读屏文案另算 —— 绝不因此丢掉 raise-to 语义（红线 #18）。
   */
  const displayLabel = expands ? btn.label.replace(/到$/u, '') : btn.label
  const amountText = btn.amount === null ? '' : formatChips(btn.amount)
  const aria =
    btn.amount === null
      ? btn.label
      : btn.amountSemantics === 'to'
        ? // label 自带「到」时不再补一个，否则读屏会念成「加注到 到 400」
          `${btn.label}${btn.label.endsWith('到') ? '' : ' 到'} ${amountText}`
        : `${btn.label} ${amountText}`
  const ariaLabel = btn.confirm
    ? `${aria}，长按确认`
    : expands
      ? // 收起态的加注按钮不显示金额（金额在抽屉里选），但读屏必须说清 raise-to 语义
        `${displayLabel}，展开加注面板${btn.amount === null ? '' : `，默认加注到 ${amountText}`}`
      : aria

  return (
    <button
      type="button"
      className="adock__btn pressable"
      data-tone={btn.tone}
      data-rank={rank}
      data-confirm={btn.confirm ? 'on' : 'off'}
      style={{ flexGrow: grow }}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-expanded={expands ? false : undefined}
      onPointerDown={handleDown}
      onPointerUp={handleUp}
      onPointerLeave={handleAbort}
      onPointerCancel={handleAbort}
      // 右键菜单 / 长按放大镜会打断长按确认
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="adock__btn-hold" ref={holdRef} aria-hidden="true" />
      <span className="adock__btn-body">
        <span className="adock__btn-label">
          {displayLabel}
          {expands && <ChevronUp />}
        </span>
        {/* expands 时刻意不印金额：抽屉里那个大号数字才是唯一权威，
            两处同时显示会让「按钮上的 400」看起来像「点一下就加注到 400」 */}
        {!expands && btn.amount !== null && (
          <span className="adock__btn-amt num">{children ?? formatChips(btn.amount)}</span>
        )}
        {/* 只保留「长按确认」这类**操作提示**；其余说明一律移到上方状态行，
            免得 44px 的按钮被挤出边界 */}
        {btn.confirm && btn.sub !== null && <span className="adock__btn-sub">{btn.sub}</span>}
      </span>
    </button>
  )
}

// ============================================================================
// ActionDock
// ============================================================================

export function ActionDock({
  onSubmit,
  legal: legalProp,
  bigBlind: bbProp,
  allinConfirm = 'longpress',
  preAction: preActionEnabled = true,
  remainingMs = null,
  onDockStateChange,
  idleCta = null,
  idleNote = null,
  idleTrail = null,
  statusOverride = null,
}: ActionDockProps): ReactNode {
  const storeLegal = useTableStore((s) => s.legal)
  const view = useTableStore((s) => s.view)
  const seats = useTableStore((s) => s.seats)
  const heroSeat = useTableStore((s) => s.heroSeat)
  const pending = useTableStore((s) => s.pending)
  const setPending = useTableStore((s) => s.setPending)
  const haptics = useSettingsStore((s) => s.haptics)
  const showToast = useUiStore((s) => s.showToast)

  const legal = legalProp === undefined ? storeLegal : legalProp
  const bigBlind = bbProp ?? view?.config.bigBlind ?? 1
  /** 对客户端是个不透明单调计数器：它一变就说明牌桌局面变了 */
  const actionNo = view?.actionLog.length ?? -1
  const street = view?.street ?? null
  const isHeroTurn =
    legal !== null && legal.onAction && (heroSeat === null || legal.seat === heroSeat)

  const plan = useMemo(
    () => (isHeroTurn ? buildDockPlan(legal, { allinConfirm }) : null),
    [isHeroTurn, legal, allinConfirm],
  )

  const [trayOpen, setTrayOpen] = useState(false)
  // 惰性初始化而不是先 0 再靠 effect 纠正：首帧就是正确金额，抽屉里那个大号数字
  // 不会有「0 闪一下再跳到 400」的中间帧（服务端渲染同理）
  const [raiseTo, setRaiseTo] = useState<Chips>(() =>
    legal !== null && legal.canRaise ? defaultRaiseTo(legal) : 0,
  )
  const [ready, setReady] = useState(false)
  const [escapeOpen, setEscapeOpen] = useState(false)
  const [slow, setSlow] = useState(false)
  const [choice, setChoice] = useState<PreActionChoice | null>(null)
  const [armed, setArmed] = useState<{ intent: ActionIntent; label: string } | null>(null)

  const rootRef = useRef<HTMLDivElement>(null)
  const pointerRef = useRef<number | null>(null)
  const mirrorRef = useRef<HTMLSpanElement>(null)
  const armedForRef = useRef<string>('')
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const acting = isHeroTurn && plan !== null && legal !== null
  // 牌桌内始终保留同一份 --dock-h：等待 → 行动不能改变舞台高度，否则整张牌桌会
  // 随回合上下跳。bar 仍留给大厅等真正独立的紧凑状态条使用。
  const dockState: DockState = trayOpen ? 'sheet' : 'actions'
  useDockState(dockState, rootRef, onDockStateChange)

  // ── 金额变更冷却（§11.5 防误触第 3 条）：actionNo 一变，主按钮先 disabled 220ms，
  //    免得对手加注后你正好点在原本「跟注」的位置上。冷却期间按下不排队、不产生动作。
  useEffect(() => {
    setTrayOpen(false)
    setEscapeOpen(false)
    if (!isHeroTurn) {
      setReady(false)
      return undefined
    }
    setReady(false)
    const t = setTimeout(() => setReady(true), ACTION_COOLDOWN_MS)
    return () => clearTimeout(t)
  }, [actionNo, isHeroTurn])

  // 面板每次重开都从默认金额起步：沿用上一手的选值是最隐蔽的一类误操作
  useEffect(() => {
    setRaiseTo(legal !== null && legal.canRaise ? defaultRaiseTo(legal) : 0)
  }, [legal])

  // 预选的生命周期之一：每条街开始清空（§6.8）
  useEffect(() => {
    setChoice(null)
  }, [street, view?.handId])

  /** 撤销反悔窗口。定时器必须一起清掉，否则「点击取消」只是让横幅消失，动作照样飞出去。 */
  const disarm = useCallback((): void => {
    if (armTimerRef.current !== null) {
      clearTimeout(armTimerRef.current)
      armTimerRef.current = null
    }
    setArmed(null)
  }, [])

  const submit = useCallback(
    (intent: ActionIntent): void => {
      if (armTimerRef.current !== null) {
        clearTimeout(armTimerRef.current)
        armTimerRef.current = null
      }
      setTrayOpen(false)
      setEscapeOpen(false)
      setArmed(null)
      setChoice(null)
      pointerRef.current = null
      // 乐观 UI 只做按钮态，牌桌状态等服务端/引擎的下一帧（§11.5）
      setPending(intent)
      onSubmit(intent)
    },
    [onSubmit, setPending],
  )

  // ── 预选执行：轮到自己 → 校验 → 250ms 反悔窗口 → 提交
  //    ★ 这里刻意**不**在成功分支里 setChoice(null)：choice 是本 effect 的依赖，
  //      清空会立刻触发 cleanup 把那颗 250ms 定时器杀掉，预选就永远不会执行。
  //      「使用后清空」交给 submit 做。失败分支没有定时器，清空是安全的。
  useEffect(() => {
    if (!isHeroTurn || legal === null || choice === null) return undefined
    // 键必须带 handId：actionNo 每手都从 0 重新数，光比它会让下一手的同号节点被误判成「已兑现」
    const turnKey = `${view?.handId ?? ''}:${actionNo}`
    if (armedForRef.current === turnKey) return undefined
    armedForRef.current = turnKey
    const r = resolvePreAction(choice, legal, view?.betToMatch ?? 0)
    if (!r.ok) {
      setChoice(null)
      if (r.reason !== null) showToast(r.reason, 'warn')
      return undefined
    }
    // 剩余时间不够时跳过反悔窗口：窗口本身把玩家送进超时判决才是最坏结果
    if (remainingMs !== null && remainingMs < PRE_ACTION_SKIP_DELAY_UNDER_MS) {
      submit(r.intent)
      return undefined
    }
    setArmed({ intent: r.intent, label: r.label })
    armTimerRef.current = setTimeout(() => {
      armTimerRef.current = null
      submit(r.intent)
    }, PRE_ACTION_DELAY_MS)
    return () => {
      if (armTimerRef.current !== null) {
        clearTimeout(armTimerRef.current)
        armTimerRef.current = null
      }
    }
  }, [isHeroTurn, legal, choice, actionNo, view?.handId, view?.betToMatch, remainingMs, showToast, submit])

  // ── pending：800ms 提示网络慢，3s 放回可点（重发完全相同的 intent，服务端幂等）
  //    ★ P5：放回可点时**必须出声**。旧版是静默解锁，玩家只看见按钮忽然又亮了，
  //      既不知道上一次到底有没有生效，也不知道现在该做什么。
  useEffect(() => {
    if (pending === null) {
      setSlow(false)
      return undefined
    }
    const slowTimer = setTimeout(() => setSlow(true), SLOW_HINT_MS)
    const recover = setTimeout(() => {
      setPending(null)
      showToast('还没收到确认，可以再点一次', 'warn')
    }, PENDING_RECOVER_MS)
    return () => {
      clearTimeout(slowTimer)
      clearTimeout(recover)
    }
  }, [pending, setPending, showToast])

  const closeTray = useCallback((): void => {
    setTrayOpen(false)
  }, [])

  const fire = useCallback(
    (btn: DockButton): void => {
      if (legal === null) return
      switch (btn.kind) {
        case 'fold':
          submit({ type: 'fold' })
          return
        case 'check':
          submit({ type: 'check' })
          return
        case 'call':
          submit({ type: 'call' })
          return
        case 'raise':
          if (btn.submits) {
            submit({ type: legal.raiseKind === 'bet' ? 'bet' : 'raise', to: legal.maxRaiseTo })
            return
          }
          // 打开抽屉而不是直接提交：加注是唯一带金额的动作，点一下就出手太危险
          setChoice(null) // 生命周期之三：主动展开加注面板时清空预选
          // 逃生口与抽屉都浮在 dock 上方同一块地方，同时开会叠在一起
          setEscapeOpen(false)
          setRaiseTo(defaultRaiseTo(legal))
          setTrayOpen(true)
          return
      }
    },
    [legal, submit],
  )

  const confirmRaise = useCallback((): void => {
    // ★ P5：被本地拦下的操作也要出声。走到这里说明抽屉开着时局面变了
    //   （对手全下 / 加注权未重开），静默不提交会让人以为是按钮坏了。
    if (legal === null || !legal.canRaise) {
      setTrayOpen(false)
      showToast('这一步已经不能加注了', 'warn')
      return
    }
    const to = Math.min(Math.max(raiseTo, legal.minRaiseTo), legal.maxRaiseTo)
    submit({ type: legal.raiseKind === 'bet' ? 'bet' : 'raise', to })
  }, [legal, raiseTo, submit, showToast])

  // ── 不是自己的回合：内容变成轻量状态行，但 dock 外框高度不变
  if (!acting) {
    const prospect = preActionEnabled ? computeProspect(view, heroSeat) : null
    const actorSeat = view?.actionOn ?? null
    const actorName =
      actorSeat === null ? null : (seats.find((s) => s.seat === actorSeat)?.name ?? null)
    const status =
      view === null
        ? '等待开局'
        : actorName === null
          ? '等待其他玩家行动'
          : `等待 ${actorName} 行动`
    return (
      <div className="adock adock--idle" ref={rootRef} data-mode="idle">
        <div className="dock__bar adock__idlebar">
          <span className="adock__status">
            <IconClock size={14} />
            <span className="adock__status-text">{statusOverride ?? idleNote ?? status}</span>
          </span>
          {idleCta !== null && (
            // 空闲条上没有别的主要动作：「坐下 / 回到牌局」就是这一屏唯一的主按钮
            <button type="button" className="btn btn--cta pressable adock__cta" onClick={idleCta.onPress}>
              {idleCta.kind === 'sit' ? '坐下' : '回到牌局'}
            </button>
          )}
          {idleCta === null && idleTrail}
          {prospect !== null && (
            <PreActionBar value={choice} onChange={setChoice} prospect={prospect} />
          )}
        </div>
      </div>
    )
  }

  const busy = pending !== null
  const locked = busy || !ready || armed !== null
  const allInRaise = isAllInTo(raiseTo, legal)
  const needConfirm = allinConfirm !== 'off'
  /** 三个按钮才配重；只剩两个时均分（需求：加注按钮不渲染时两个按钮等宽） */
  const weighted = plan.buttons.length >= 3

  const confirmBtn: DockButton = {
    kind: 'raise',
    label: allInRaise ? 'ALL-IN' : legal.raiseKind === 'bet' ? '下注到' : '加注到',
    amount: raiseTo,
    amountSemantics: 'to',
    sub: allInRaise && needConfirm ? '长按确认' : null,
    span: 1,
    tone: allInRaise ? 'allin' : 'raise',
    confirm: allInRaise && needConfirm,
    submits: true,
  }
  const escapeFoldBtn: DockButton = {
    kind: 'fold',
    label: '弃牌',
    amount: null,
    amountSemantics: null,
    sub: '长按确认',
    span: 1,
    tone: 'fold',
    confirm: true,
    submits: true,
  }

  /** 状态行：一行 24px 的次要文字。信息在这里，操作在下面那行，互不挤占。 */
  const infoSub = plan.buttons.find((b) => !b.confirm && b.sub !== null)?.sub ?? null

  return (
    <div
      className="adock adock--act"
      ref={rootRef}
      data-mode="act"
      data-busy={busy ? 'on' : 'off'}
      // 抽屉开合的兜底开关（见 action.css：外层 .dock 的 data-state 不是本组件渲染的）
      data-sheet={trayOpen ? 'on' : 'off'}
    >
      <div className="adock__meta">
        {busy ? (
          <span className="adock__pendingtxt">
            <span className="adock__spinner" aria-hidden="true" />
            {slow ? '网络较慢' : '已提交，等待确认'}
          </span>
        ) : armed !== null ? (
          <span className="adock__note">即将{armed.label}</span>
        ) : (
          <>
            {plan.note !== null && <span className="adock__note">{plan.note}</span>}
            {plan.note === null && infoSub !== null && (
              <span className="adock__info">{infoSub}</span>
            )}
            {/* 能过牌时 Fold 不进主行，逃生口收在这里且要长按（§11.5 防误触第 1 条） */}
            {plan.escapeFold && (
              <button
                type="button"
                className="adock__escape hit-expand"
                aria-expanded={escapeOpen}
                onClick={() => setEscapeOpen((v) => !v)}
              >
                {escapeOpen ? '收起' : '更多'}
              </button>
            )}
          </>
        )}
      </div>

      {/* 逃生口弹层是 .adock 的直接子节点而不是状态行的：状态行要 overflow:hidden
          来截断过长的提示文字，弹层挂在里面会被一起裁掉 */}
      {escapeOpen && !busy && armed === null && (
        <div className="adock__escape-pop">
          <PressButton
            btn={escapeFoldBtn}
            disabled={locked}
            haptics={haptics}
            holdMs={ESCAPE_FOLD_HOLD_MS}
            pointerRef={pointerRef}
            rank="quiet"
            onFire={() => submit({ type: 'fold' })}
          />
        </div>
      )}

      {/* 反悔窗口期间整行换成一个大号「取消」：位置与高度和主按钮行完全一致，
          所以不会有任何布局位移，而取消的命中区又大到不可能点不中 */}
      {armed !== null ? (
        <div className="adock__row">
          <button type="button" className="adock__armed pressable" onClick={disarm}>
            <IconClose size={18} />
            <span>点击取消</span>
          </button>
        </div>
      ) : (
        <div className="adock__row" role="group" aria-label="行动">
          {plan.buttons.map((b) => (
            <PressButton
              key={b.kind}
              btn={b}
              disabled={locked}
              haptics={haptics}
              grow={weighted ? GROW[b.kind] : 1}
              rank={RANK[b.kind]}
              expands={b.kind === 'raise' && !b.submits}
              pointerRef={pointerRef}
              onFire={() => fire(b)}
            />
          ))}
        </div>
      )}

      {/* ── 底部抽屉：加注尺度面板 ─────────────────────────────────
          常驻在 DOM 里（靠 .dock[data-state] 切显隐）才能播出场/退场动画；
          关闭态是 visibility:hidden，既不可点也不进读屏与 Tab 序。
          absolute 锚在 dock 上边缘 → 展开时牌桌不会被顶上去。 */}
      {plan.canOpenRaise && (
        <div className="dock__sheet" role="group" aria-label="加注尺度" aria-hidden={!trayOpen}>
          <div className="dock__sheet-handle" aria-hidden="true" />
          <RaiseControl
            legal={legal}
            bigBlind={bigBlind}
            value={raiseTo}
            onChange={setRaiseTo}
            mirrorRef={mirrorRef}
            haptics={haptics}
            onClose={closeTray}
            footer={
              <PressButton
                btn={confirmBtn}
                disabled={locked}
                haptics={haptics}
                pointerRef={pointerRef}
                onFire={confirmRaise}
              >
                {/* 拖动中由 RaiseControl 直接写这个节点的 textContent，不经过 React */}
                <span ref={mirrorRef}>{formatChips(raiseTo)}</span>
              </PressButton>
            }
          />
        </div>
      )}
    </div>
  )
}

export default ActionDock
